import axios from "axios";
import { decryptData } from "../utils/encryptionUtils";

const API_URL = `${process.env.REACT_APP_API_URL}/api`; // base URL for API
const API_URL_EXTERNE = `${process.env.REACT_APP_API_URL_EXTERNE}/api`; // base URL for External API
const apiUrl = process.env.REACT_APP_IMAGE_BASE_URL;

/**
 * Fetch profiles with pagination.
 *
 * @param {Object} params - Pagination parameters.
 * @param {number} params.page - Current page number.
 * @param {number} params.limit - Number of profiles per page.
 * @returns {Promise<Object>} - API response containing profiles and total count.
 */
export const getProfiles = async ({ page = 1, limit = 10 }) => {
  try {
    const response = await axios.get(`${API_URL}/profiles`, {
      params: { page, limit },
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Erreur de récupération des profils :", error);
    throw error;
  }
};

/**
 * Search profiles with filters and pagination using POST request.
 *
 * @param {Object} filters - Filter and pagination parameters.
 * @returns {Promise<Object>} - API response containing filtered profiles and total count.
 */
export const searchProfiles = async (filters) => {
  try {
    const response = await axios.post(`${API_URL}/profiles/search`, filters, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    const decryptedData = decryptData(response.data.data);
    return { data: decryptedData, total: response.data.total };
  } catch (error) {
    console.error("Erreur de recherche des profils :", error);
    throw error;
  }
};

/**
 * Add a new profile with CV and image upload.
 *
 * @param {Object} profileData - Data of the profile to add.
 * @param {File} cvFile - CV file to upload.
 * @param {File} imageFile - Image file to upload.
 * @returns {Promise<Object>} - API response containing the added profile data.
 */
export const addProfile = async (profileData, cvFile, imageFile) => {
  try {
    const formData = new FormData();
    formData.append("profileData", JSON.stringify(profileData));
    if (cvFile) {
      formData.append("cv", cvFile);
    }
    if (imageFile) {
      formData.append("image", imageFile);
    }

    const response = await axios.post(`${API_URL}/profiles`, formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Erreur d'ajout du profil :", error);
    throw error;
  }
};

/**
 * Edit an existing profile.
 *
 * @param {number} id - Profile ID.
 * @param {Object|FormData} profileData - Data to update the profile.
 * @returns {Promise<Object>} - API response containing the updated profile data.
 */
export const editProfile = async (id, profileData) => {
  try {
    let headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };
    let dataToSend = profileData;
    if (profileData instanceof FormData) {
      headers["Content-Type"] = "multipart/form-data";
    } else {
      headers["Content-Type"] = "application/json";
      dataToSend = JSON.stringify(profileData);
    }
    const response = await axios.post(`${API_URL}/profiles/${id}`, dataToSend, {
      headers,
    });
    return response.data.profile || response.data;
  } catch (error) {
    console.error("Erreur de mise à jour du profil :", error);
    throw error;
  }
};

/**
 * Delete a profile.
 *
 * @param {number} id - Profile ID.
 * @returns {Promise<void>} - API response indicating the deletion result.
 */
export const deleteProfile = async (id) => {
  try {
    await axios.delete(`${API_URL}/profiles/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  } catch (error) {
    console.error("Erreur de suppression du profil :", error);
    throw error;
  }
};

/**
 * Get a profile by ID.
 *
 * @param {number} id - Profile ID.
 * @returns {Promise<Object>} - API response containing the profile data.
 */
export const getProfileById = async (id) => {
  try {
    const response = await axios.get(`${API_URL}/profiles/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data.profile;
  } catch (error) {
    console.error("Erreur de récupération du profil :", error);
    throw error;
  }
};

/**
 * User login.
 *
 * @param {Object} credentials - User credentials.
 * @param {string} credentials.email - User email.
 * @param {string} credentials.password - User password.
 * @returns {Promise<Object>} - API response containing the JWT token and user data.
 */
export const loginUser = async ({ email, password }) => {
  try {
    const response = await axios.post(
      `${API_URL}/login`,
      { email, password },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return {
      token: response.data.token,
      user: response.data.user, // Include user data in the response
    };
  } catch (error) {
    console.error("Erreur de connexion :", error);
    throw error;
  }
};

/**
 * User signup.
 *
 * @param {Object} userDetails - User details.
 * @param {string} userDetails.email - User email.
 * @param {string} userDetails.password - User password.
 * @param {string} userDetails.username - User username.
 * @param {string} userDetails.fullName - User full name.
 * @param {string} userDetails.address - User address.
 * @param {string} userDetails.phone - User phone number.
 * @param {string} userDetails.birthDate - User birth date.
 * @returns {Promise<Object>} - API response containing the signup result.
 */
export const signupUser = async ({
  email,
  password,
  username,
  fullName,
  address,
  phone,
  birthDate,
  companyId,
  roles,
  captchaVerification,
}) => {
  try {
    const response = await axios.post(
      `${API_URL}/signup`,
      {
        email,
        password,
        username,
        fullName,
        address,
        phone,
        birthDate,
        companyId,
        roles,
        captchaVerification,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur d'inscription :", error);
    throw error;
  }
};

/**
 * Get the current authenticated user.
 *
 * @returns {Promise<Object>} - API response containing the user data.
 */
export const getCurrentUser = async () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No token found");
    }
    const response = await axios.get(`${API_URL}/currentuser`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Erreur de récupération de l'utilisateur :", error);
    throw error;
  }
};

/**
 * Get all users.
 *
 * @returns {Promise<Object[]>} - API response containing the list of users.
 */
export const getAllUsers = async () => {
  try {
    const response = await axios.get(`${API_URL}/users`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Erreur de récupération des utilisateurs :", error);
    throw error;
  }
};

/**
 * Get a user by ID.
 *
 * @param {number} id - User ID.
 * @returns {Promise<Object>} - API response containing the user data.
 */
export const getUserById = async (id) => {
  try {
    const response = await axios.get(`${API_URL}/users/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Erreur de récupération de l'utilisateur :", error);
    throw error;
  }
};

/**
 * Modify a user.
 *
 * @param {number} id - User ID.
 * @param {Object} data - Data to modify.
 * @param {File} profilePicture - Profile picture file to upload.
 * @returns {Promise<Object>} - API response containing the modified user data.
 */
export const modifyUser = async (id, data, profilePicture) => {
  try {
    const formData = new FormData();
    formData.append('data', JSON.stringify(data));
    if (profilePicture) {
      formData.append('profilePicture', profilePicture);
    }

    const response = await axios.post(
      `${API_URL}/users/modifyuser/${id}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur de modification de l'utilisateur :", error);
    throw error;
  }
};

/**
 * Delete a user.
 *
 * @param {number} id - User ID.
 * @returns {Promise<void>} - API response indicating the deletion result.
 */
export const deleteUser = async (id) => {
  try {
    await axios.delete(`${API_URL}/users/deleteuser/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  } catch (error) {
    console.error("Erreur de suppression de l'utilisateur :", error);
    throw error;
  }
};

/**
 * Change user password.
 *
 * @param {number} id - User ID.
 * @param {string} oldPassword - Old password.
 * @param {string} newPassword - New password.
 * @returns {Promise<Object>} - API response containing the result.
 */
export const changePassword = async (id, oldPassword, newPassword) => {
  try {
    const response = await axios.put(
      `${API_URL}/users/changepassword/${id}`,
      { oldPassword, newPassword },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur de changement de mot de passe :", error);
    throw error;
  }
};

/**
 * Block or unblock a user.
 *
 * @param {number} id - User ID.
 * @param {boolean} isBlocked - Block status.
 * @returns {Promise<Object>} - API response containing the updated user data.
 */
export const blockUser = async (id, isBlocked) => {
  try {
    const response = await axios.put(
      `${API_URL}/users/block/${id}`,
      { isBlocked },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur de blocage de l'utilisateur :", error);
    throw error;
  }
};

/**
 * Get users assigned to the current user.
 *
 * @returns {Promise<Object[]>} - API response containing the list of assigned users.
 */
export const getAssignedUsers = async () => {
  try {
    const response = await axios.get(`${API_URL}/assigned-users`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Erreur de récupération des utilisateurs assignés :", error);
    throw error;
  }
};

/**
 * Fetch profiles based on description and number of profiles.
 *
 * @param {Object} params - Parameters for the request.
 * @param {string} params.description - Description to search profiles.
 * @param {number} params.numberOfProfiles - Number of profiles to fetch.
 * @returns {Promise<Object>} - API response containing profiles.
 */
export const getProfilesByDescription = async ({
  description,
  numberOfProfiles,
}) => {
  try {
    const response = await axios.post(
      `${API_URL_EXTERNE}/search_candidates`,
      {
        description,
        numberOfProfiles,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    const decryptedData = decryptData(response.data.data);
    if (decryptedData.message) {
      return { message: decryptedData.message, data: [], total: 0 };
    } else {
      return { data: decryptedData.data };
    }
  } catch (error) {
    console.error(
      "Erreur de récupération des profils par description :",
      error
    );
    throw error;
  }
};

/**
 * Upload a CV for a user.
 *
 * @param {number} userId - User ID.
 * @param {File} cvFile - CV file to upload.
 * @returns {Promise<Object>} - API response containing the upload result.
 */
export const uploadCV = async (userId, cvFile) => {
  try {
    const formData = new FormData();
    formData.append("cv", cvFile);

    const response = await axios.post(`${API_URL}/upload-cv`, formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Erreur de téléchargement du CV :", error);
    throw error;
  }
};

/**
 * Save conversation.
 *
 * @param {Object} conversationData - Data of the conversation to save.
 * @returns {Promise<Object>} - API response containing the saved conversation data.
 */
export const saveConversation = async (conversationData) => {
  try {
    const response = await axios.post(
      `${API_URL}/conversations`,
      conversationData,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur d'enregistrement de la conversation :", error);
    throw error;
  }
};

/**
 * Fetch conversations for the current user.
 *
 * @returns {Promise<Object>} - API response containing the user's conversations.
 */
export const getUserConversations = async () => {
  try {
    const response = await axios.get(
      `${API_URL}/conversations/userConversations`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    const decryptedData = decryptData(response.data.conversations);
    return decryptedData;
  } catch (error) {
    console.error(
      "Erreur de récupération des conversations de l'utilisateur :",
      error
    );
    throw error;
  }
};

/**
 * Fetch all conversations.
 *
 * @returns {Promise<Object>} - API response containing all conversations.
 */
export const getAllConversations = async () => {
  try {
    const response = await axios.get(`${API_URL}/conversations`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Erreur de récupération des conversations :", error);
    throw error;
  }
};

/**
 * Delete a conversation.
 *
 * @param {number} id - Conversation ID.
 * @returns {Promise<void>} - API response indicating the deletion result.
 */
export const deleteConversation = async (id) => {
  try {
    await axios.delete(`${API_URL}/conversations/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  } catch (error) {
    console.error("Erreur de suppression de la conversation :", error);
    throw error;
  }
};

/**
 * Save a search.
 *
 * @param {Object} search - Search data.
 * @returns {Promise<Object>} - API response.
 */
export const saveSearch = async (search) => {
  try {
    const response = await axios.post(`${API_URL}/searches`, search, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Erreur d'enregistrement de la recherche :", error);
    throw error;
  }
};

/**
 * Delete a search.
 *
 * @param {number} id - Search ID.
 * @returns {Promise<void>} - API response.
 */
export const deleteSearch = async (id) => {
  try {
    await axios.delete(`${API_URL}/searches/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  } catch (error) {
    console.error("Erreur de suppression de la recherche :", error);
    throw error;
  }
};

/**
 * Get user searches.
 *
 * @returns {Promise<Object[]>} - API response containing the list of searches.
 */
export const getUserSearches = async () => {
  try {
    const response = await axios.get(`${API_URL}/user-searches`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    const decryptedData = decryptData(response.data.searches);
    return decryptedData;
  } catch (error) {
    console.error(
      "Erreur de récupération des recherches de l'utilisateur :",
      error
    );
    throw error;
  }
};

/**
 * Sort searches.
 *
 * @param {Object} sortParams - Sorting parameters.
 * @param {string} sortParams.field - Field to sort by.
 * @param {string} sortParams.order - Sort order ('asc' or 'desc').
 * @returns {Promise<Object[]>} - API response containing the sorted searches.
 */
export const sortSearches = async ({ field, order }) => {
  try {
    const response = await axios.get(`${API_URL}/searches/sort`, {
      params: { field, order },
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data.searches;
  } catch (error) {
    console.error("Erreur de tri des recherches :", error);
    throw error;
  }
};

/**
 * Verify account with the provided code.
 *
 * @param {string} code - Verification code.
 * @returns {Promise<Object>} - API response containing the verification result.
 */
export const verifyAccount = async (code) => {
  try {
    const response = await axios.post(
      `${API_URL}/verify-account`,
      { code },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur de vérification du compte :", error);
    throw error;
  }
};

/**
 * Send verification code to the current authenticated user.
 *
 * @returns {Promise<Object>} - API response containing the result.
 */
export const sendVerificationCode = async () => {
  try {
    const response = await axios.post(
      `${API_URL}/send-verification-code`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur d'envoi du code de vérification :", error);
    throw error;
  }
};

/**
 * Send reset password link to the provided email with captcha verification.
 *
 * @param {Object} data - Data containing email and captcha verification.
 * @param {string} data.email - User email.
 * @param {Object} data.captchaVerification - Captcha verification data.
 * @returns {Promise<Object>} - API response.
 */
export const sendResetPasswordLink = async ({ email, captchaVerification }) => {
  try {
    const response = await axios.post(
      `${API_URL}/send-reset-password-link`,
      { email, captchaVerification },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur d'envoi du lien de réinitialisation :", error);
    throw error;
  }
};

/**
 * Reset password using the token from the URL.
 *
 * @param {string} token - Reset token.
 * @param {string} newPassword - New password.
 * @param {string} confirmNewPassword - Confirm new password.
 * @returns {Promise<Object>} - API response.
 */
export const resetPassword = async (token, newPassword, confirmNewPassword) => {
  try {
    const response = await axios.post(
      `${API_URL}/reset-password/${token}`,
      { newPassword, confirmNewPassword },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur de réinitialisation du mot de passe :", error);
    throw error;
  }
};

/**
 * Update profiles in the external API.
 *
 * @param {number} id - Profile ID.
 * @returns {Promise<Object>} - API response.
 */
export const updateProfiles = async (id) => {
  try {
    const response = await axios.put(
      `${API_URL_EXTERNE}/update_profiles`,
      { id },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur de mise à jour des profils :", error);
    throw error;
  }
};

/**
 * Fetch captcha data.
 *
 * @returns {Promise<Object>} - API response containing captcha data.
 */
export const getCaptcha = async () => {
  try {
    const response = await axios.get(`${API_URL}/captcha`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement du captcha:", error);
    throw error;
  }
};

/**
 * Verify captcha.
 *
 * @param {Object} data - Captcha verification data.
 * @param {string} data.captchaId - Captcha ID.
 * @param {Array<string>} data.selectedImages - Selected images.
 * @returns {Promise<Object>} - API response containing verification result.
 */
export const verifyCaptcha = async (data) => {
  try {
    const response = await axios.post(`${API_URL}/captcha/verify`, data, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la vérification du captcha:", error);
    throw error;
  }
};

/**
 * Fetch profiles of the current user.
 *
 * @returns {Promise<Object>} - API response containing profiles.
 */
export const getProfilesOfCurrentUser = async () => {
  try {
    const response = await axios.get(`${API_URL}/profiles/current-user`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error("Failed to fetch profiles of the current user.");
  }
};

/**
 * Get image URL.
 *
 * @param {string} imagePath - Path of the image.
 * @returns {Promise<string>} - URL of the image.
 */
export const getImageUrl = async (imagePath) => {
  const token = localStorage.getItem("token"); // Ensure the correct token key is used
  if (!token) {
    throw new Error("No token found");
  }
  const response = await axios.get(`${apiUrl}/${imagePath}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    responseType: "blob",
  });
  return URL.createObjectURL(response.data);
};

/**
 * Convert speech to text using an external API.
 *
 * @param {File} audioFile - Audio file (wav format) to convert.
 * @returns {Promise<Object>} - API response containing the converted text.
 */
export const speechToText = async (audioFile) => {
  try {
    const formData = new FormData();
    formData.append("audio", audioFile);

    const response = await axios.post(
      `${API_URL_EXTERNE}/speech-to-text`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur de conversion de la parole en texte :", error);
    throw error;
  }
};

/**
 * Chat with the assistant using an external API.
 *
 * @param {Object} userInput - User input for the chat.
 * @param {Function} dispatch - Redux dispatch function.
 * @param {Function} logoutAction - Logout action to dispatch.
 * @returns {Promise<Object>} - API response containing the chat response or data.
 */
export const chatWithAssistant = async (userInput, dispatch, logoutAction) => {
  try {
    const response = await axios.post(`${API_URL_EXTERNE}/chat`, userInput, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    // Decrypt the response data
    const decryptedData = decryptData(response.data.data);

    // Check if the response indicates a logout or redirection action
    if (decryptedData.action === "logout") {
      dispatch(logoutAction());
      return { message: "Redirection vers la page de déconnexion." };
    } else if (decryptedData.action === "see profile") {
      const redirectEvent = new CustomEvent("redirectToMyProfile");
      window.dispatchEvent(redirectEvent);
    } else if (decryptedData.action === "view_chat_history") {
      const redirectEvent = new CustomEvent("navigateToComponent", {
        detail: { component: "historique" },
      });
      window.dispatchEvent(redirectEvent);
    } else if (decryptedData.action === "add_profile") {
      const redirectEvent = new CustomEvent("navigateToComponent", {
        detail: { component: "addprofile" },
      });
      window.dispatchEvent(redirectEvent);
    } else if (decryptedData.action === "view_search_history") {
      const redirectEvent = new CustomEvent("navigateToComponent", {
        detail: { component: "searchManagement" },
      });
      window.dispatchEvent(redirectEvent);
    } else if (decryptedData.action === "see_profile_candidature") {
      const redirectEvent = new CustomEvent("navigateToComponent", {
        detail: { component: "seeProfileCondidature" },
      });
      window.dispatchEvent(redirectEvent);
    } else if (decryptedData.action === "sql") {
      const redirectEvent = new CustomEvent("navigateToComponent", {
        detail: { component: "profiles" },
      });
      window.dispatchEvent(redirectEvent);
    }

    return decryptedData;
  } catch (error) {
    console.error("Erreur de communication avec l'assistant :", error);
    throw error;
  }
};

/**
 * Get the profile picture of the current user.
 *
 * @param {string} filename - The filename of the profile picture.
 * @returns {Promise<string>} - URL of the profile picture.
 */
export const getUserProfilePicture = async (filename) => {
  try {
    const response = await axios.get(`${API_URL}/images/user/${filename}?t=${new Date().getTime()}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      responseType: "blob",
    });
    return URL.createObjectURL(response.data);
  } catch (error) {
    console.error("Erreur de récupération de la photo de profil :", error);
    throw error;
  }
};

/**
 * User logout.
 *
 * @returns {Promise<void>} - API response indicating the logout result.
 */
export const logoutUser = async () => {
  try {
    await axios.post(
      `${API_URL}/logout`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
  } catch (error) {
    console.error("Erreur de déconnexion :", error);
    throw error;
  }
};

/**
 * Fetch all AppelOffres.
 *
 * @returns {Promise<Object[]>} - API response containing the list of AppelOffres.
 */
export const getAllAppelOffres = async () => {
  try {
    const response = await axios.get(`${API_URL}/appel-offre`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Erreur de récupération des AppelOffres :", error);
    throw error;
  }
};

/**
 * Fetch an AppelOffre by ID.
 *
 * @param {number} id - AppelOffre ID.
 * @returns {Promise<Object>} - API response containing the AppelOffre data.
 */
export const getAppelOffreById = async (id) => {
  try {
    const response = await axios.get(`${API_URL}/appel-offre/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Erreur de récupération de l'AppelOffre :", error);
    throw error;
  }
};

/**
 * Create a new AppelOffre.
 *
 * @param {Object} appelOffreData - Data of the AppelOffre to create.
 * @returns {Promise<Object>} - API response containing the created AppelOffre data.
 */
export const createAppelOffre = async (appelOffreData) => {
  try {
    const response = await axios.post(`${API_URL}/appel-offre`, appelOffreData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Erreur de création de l'AppelOffre :", error);
    throw error;
  }
};

/**
 * Update an existing AppelOffre.
 *
 * @param {number} id - AppelOffre ID.
 * @param {Object} appelOffreData - Data to update the AppelOffre.
 * @returns {Promise<Object>} - API response containing the updated AppelOffre data.
 */
export const updateAppelOffre = async (id, appelOffreData) => {
  try {
    const response = await axios.put(`${API_URL}/appel-offre/${id}`, appelOffreData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Erreur de mise à jour de l'AppelOffre :", error);
    throw error;
  }
};

/**
 * Delete an AppelOffre.
 *
 * @param {number} id - AppelOffre ID.
 * @returns {Promise<void>} - API response indicating the deletion result.
 */
export const deleteAppelOffre = async (id) => {
  try {
    await axios.delete(`${API_URL}/appel-offre/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  } catch (error) {
    console.error("Erreur de suppression de l'AppelOffre :", error);
    throw error;
  }
};

/**
 * Fetch AppelOffres for the current user.
 *
 * @returns {Promise<Object[]>} - API response containing the list of AppelOffres for the current user.
 */
export const getAppelOffresForCurrentUser = async () => {
  try {
    const response = await axios.get(`${API_URL}/appel-offre/me`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Erreur de récupération des AppelOffres de l'utilisateur :", error);
    throw error;
  }
};

/**
 * Create a new candidature.
 *
 * @param {Object} candidatureData - Data for the candidature.
 * @returns {Promise<Object>} - API response containing the created candidature.
 */
export const createCandidature = async (candidatureData) => {
  try {
    const response = await axios.post(`${API_URL}/candidatures`, candidatureData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Erreur de création de la candidature :", error);
    throw error;
  }
};

/**
 * Update the status of a candidature.
 *
 * @param {number} id - Candidature ID.
 * @param {string} status - New status for the candidature (en cours, annulé, accepté).
 * @returns {Promise<Object>} - API response containing the updated candidature.
 */
export const updateCandidatureStatus = async (id, status) => {
  try {
    const response = await axios.put(
      `${API_URL}/candidatures/${id}/status`,
      { status },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur de mise à jour du statut de la candidature :", error);
    throw error;
  }
};

/**
 * Fetch all candidatures.
 *
 * @returns {Promise<Object[]>} - API response containing the list of all candidatures.
 */
export const getAllCandidatures = async () => {
  try {
    const response = await axios.get(`${API_URL}/candidatures`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Erreur de récupération des candidatures :", error);
    throw error;
  }
};

/**
 * Fetch candidatures of the current user.
 *
 * @returns {Promise<Object[]>} - API response containing the user's candidatures.
 */
export const getUserCandidatures = async () => {
  try {
    const response = await axios.get(`${API_URL}/mesCandidatures`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data; 
  } catch (error) {
    console.error("Erreur de récupération des candidatures de l'utilisateur :", error);
    throw error;
  }
};

/**
 * Delete a candidature.
 *
 * @param {number} id - Candidature ID.
 * @returns {Promise<void>} - API response indicating the deletion result.
 */
export const deleteCandidature = async (id) => {
  try {
    await axios.delete(`${API_URL}/candidatures/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  } catch (error) {
    console.error("Erreur de suppression de la candidature :", error);
    throw error;
  }
};

/**
 * Fetch candidatures by AppelOffre ID.
 *
 * @param {number} appelOffreId - ID of the AppelOffre.
 * @returns {Promise<Object[]>} - API response containing the list of candidatures.
 */
export const getCandidaturesByAppelOffre = async (appelOffreId) => {
  try {
    const response = await axios.get(`${API_URL}/appelOffres/${appelOffreId}/candidatures`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data.candidatures;
  } catch (error) {
    console.error("Erreur de récupération des candidatures :", error);
    throw error;
  }
};

/**
 * Fetch candidatures for AppelOffres created by the current user or their collaborators.
 *
 * @returns {Promise<Object[]>} - API response containing the list of candidatures.
 */
export const getCandidaturesByAppelOffreOwner = async () => {
  try {
    const response = await axios.get(`${API_URL}/myAppelOffres/candidatures`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data.candidatures;
  } catch (error) {
    console.error("Erreur de récupération des candidatures :", error);
    throw error;
  }
};

/**
 * Fetch sorted candidatures based on status or date.
 *
 * @param {Object} params - Sorting parameters.
 * @param {string} params.sort - Field to sort by (e.g., 'createdAt', 'status').
 * @param {string} params.order - Sort order ('ASC' or 'DESC').
 * @param {string} [params.status] - Optional status filter ('retenue', 'refusée', 'en attente').
 * @returns {Promise<Object[]>} - API response containing sorted candidatures.
 */
export const getSortedCandidatures = async ({ sort, order, status }) => {
  try {
    const response = await axios.get(`${API_URL}/candidatures/sort`, {
      params: { sort, order, status }, // Include status in the query parameters
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data.candidatures;
  } catch (error) {
    console.error("Erreur de tri des candidatures :", error);
    throw error;
  }
};

/**
 * Search AppelOffres by keyword.
 *
 * @param {string} keyword - The search keyword.
 * @returns {Promise<Object[]>} - API response containing the list of matching AppelOffres.
 */
export const searchAppelOffres = async (keyword) => {
  try {
    const response = await axios.get(`${API_URL}/appel-offre/search`, {
      params: { keyword },
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Erreur de recherche des AppelOffres :", error);
    throw error;
  }
};

/**
 * Search candidatures by AppelOffre title.
 *
 * @param {string} titre - The title to search for.
 * @returns {Promise<Object[]>} - API response containing the list of matching candidatures.
 */
export const searchCandidaturesByAppelOffreTitle = async (titre) => {
  try {
    const response = await axios.get(`${API_URL}/candidatures/search`, {
      params: { titre }, 
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data.candidatures;
  } catch (error) {
    console.error("Erreur de recherche des candidatures :", error);
    throw error;
  }
};
